import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, 
  FileText, 
  BookOpen, 
  BarChart3, 
  Plus, 
  Trash, 
  RefreshCw, 
  TrendingUp, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Wifi, 
  WifiOff,
  Activity,
  Target,
  DollarSign,
  Shield,
  AlertTriangle,
  MessageCircle,
  MessageSquare,
  Wand2,
  Heart,
  Video,
  Calculator,
  Type,
  Layers,
  Zap,
  Star,
  Bookmark,
  Play,
  Pause,
  Volume2,
  Trophy,
  GraduationCap,
  Building2,
  Crown,
  Settings,
  UserCheck
} from 'lucide-react';
import { useRealtime } from '../../contexts/RealtimeContext';
import { useNavigate } from 'react-router-dom';
import { formatTimestamp } from '../../utils/timestampUtils';
import { getAllAdminPermissions, getAllAdmins } from '../../firebase/collections';

const FounderDashboard = () => {
  const { adminData, isConnected, connectionStatus, logAdminAction, currentUser } = useRealtime();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [activeTab, setActiveTab] = useState('overview');
  const [adminStats, setAdminStats] = useState({
    totalAdmins: 0,
    activeAdmins: 0,
    adminsWithPermissions: 0
  });
  const navigate = useNavigate();

  // Calculate real statistics from Firebase data
  const stats = {
    totalUsers: adminData?.users?.length || 0,
    totalTests: adminData?.testResults?.length || 0,
    totalCourses: adminData?.courses?.length || 0,
    totalQuestions: adminData?.questions?.length || 0,
    totalDoubts: adminData?.doubts?.length || 0,
    totalHelpRequests: adminData?.helpRequests?.length || 0,
    totalTodos: adminData?.todos?.length || 0,
    totalVideos: adminData?.videos?.length || 0,
    averageScore: adminData?.analytics?.averageScore || 0,
    completionRate: adminData?.analytics?.completionRate || 0,
    activeUsers: adminData?.analytics?.activeUsers || 0
  };

  // Calculate revenue (mock calculation - replace with real data)
  const revenue = (223 + stats.totalUsers) * 99; // Assuming ₹99 per user with 223+ formula

  // Calculate recent activity metrics
  const recentActivity = (adminData?.adminActions?.slice(0, 5) || []).filter(action => 
    action && 
    typeof action === 'object' && 
    action !== null && 
    !Array.isArray(action)
  );

  useEffect(() => {
    loadAdminStats();
    if (isConnected) {
      setLoading(false);
      setLastUpdated(new Date());
    }
  }, [isConnected]);

  const loadAdminStats = async () => {
    try {
      const [adminsResult, permissionsResult] = await Promise.all([
        getAllAdmins(),
        getAllAdminPermissions()
      ]);

      if (adminsResult.success && permissionsResult.success) {
        const admins = adminsResult.data || [];
        const permissions = permissionsResult.data || [];
        
        setAdminStats({
          totalAdmins: admins.length,
          activeAdmins: admins.filter(a => a.isActive !== false).length,
          adminsWithPermissions: permissions.length
        });
      }
    } catch (error) {
      console.error('Error loading admin stats:', error);
    }
  };

  const systemHealth = {
    server: 'online',
    database: connectionStatus === 'connected' ? 'connected' : 'disconnected',
    storage: 75,
    performance: 'good',
    uptime: '99.9%'
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Crown className="h-8 w-8 text-yellow-500" />
            Founder Dashboard
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Complete system overview and admin management
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            {isConnected ? (
              <>
                <Wifi className="h-4 w-4 text-green-500" />
                <span>Connected</span>
              </>
            ) : (
              <>
                <WifiOff className="h-4 w-4 text-red-500" />
                <span>Disconnected</span>
              </>
            )}
          </div>
          <button
            onClick={() => {
              setLastUpdated(new Date());
              loadAdminStats();
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Admin Management Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 border-l-4 border-yellow-500 cursor-pointer hover:shadow-xl transition-shadow"
          onClick={() => navigate('/founder/admin-management')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Admins</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">
                {adminStats.totalAdmins}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                {adminStats.activeAdmins} active
              </p>
            </div>
            <div className="bg-yellow-100 dark:bg-yellow-900/30 p-3 rounded-full">
              <UserCheck className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
            </div>
          </div>
        </motion.div>

        {/* Total Users */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 border-l-4 border-blue-500"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Users</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">
                {(223 + stats.totalUsers).toLocaleString()}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                {stats.activeUsers} active
              </p>
            </div>
            <div className="bg-blue-100 dark:bg-blue-900/30 p-3 rounded-full">
              <Users className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </motion.div>

        {/* Total Tests */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 border-l-4 border-green-500"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Tests</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">
                {stats.totalTests.toLocaleString()}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                Avg Score: {stats.averageScore.toFixed(1)}%
              </p>
            </div>
            <div className="bg-green-100 dark:bg-green-900/30 p-3 rounded-full">
              <FileText className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
          </div>
        </motion.div>

        {/* Revenue */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 border-l-4 border-purple-500"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Estimated Revenue</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">
                ₹{revenue.toLocaleString()}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                From {(223 + stats.totalUsers)} users
              </p>
            </div>
            <div className="bg-purple-100 dark:bg-purple-900/30 p-3 rounded-full">
              <DollarSign className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 cursor-pointer hover:shadow-xl transition-shadow"
          onClick={() => navigate('/founder/admin-management')}
        >
          <div className="flex items-center gap-4">
            <div className="bg-yellow-100 dark:bg-yellow-900/30 p-3 rounded-lg">
              <UserCheck className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">Manage Admins</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Configure admin permissions and access
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 cursor-pointer hover:shadow-xl transition-shadow"
          onClick={() => navigate('/founder/admin-logs')}
        >
          <div className="flex items-center gap-4">
            <div className="bg-blue-100 dark:bg-blue-900/30 p-3 rounded-lg">
              <Activity className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">View Admin Logs</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Monitor daily admin activities
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 cursor-pointer hover:shadow-xl transition-shadow"
          onClick={() => navigate('/admin')}
        >
          <div className="flex items-center gap-4">
            <div className="bg-green-100 dark:bg-green-900/30 p-3 rounded-lg">
              <Shield className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">Admin Panel</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Access full admin dashboard
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* System Health */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">System Health</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-full ${systemHealth.server === 'online' ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30'}`}>
              {systemHealth.server === 'online' ? (
                <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
              ) : (
                <XCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
              )}
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">Server</p>
              <p className="text-xs text-gray-600 dark:text-gray-400">{systemHealth.server}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-full ${systemHealth.database === 'connected' ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30'}`}>
              {systemHealth.database === 'connected' ? (
                <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
              ) : (
                <XCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
              )}
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">Database</p>
              <p className="text-xs text-gray-600 dark:text-gray-400">{systemHealth.database}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-900/30">
              <Activity className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">Uptime</p>
              <p className="text-xs text-gray-600 dark:text-gray-400">{systemHealth.uptime}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Admin Activity */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Recent Admin Activity</h2>
        <div className="space-y-3">
          {recentActivity.length > 0 ? (
            recentActivity.map((action, index) => (
              <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <Activity className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {action.action || 'Unknown Action'}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    {action.timestamp ? formatTimestamp(action.timestamp) : 'Recently'}
                  </p>
                </div>
              </div>
            ))
          ) : (
            <p className="text-sm text-gray-600 dark:text-gray-400 text-center py-4">
              No recent activity
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default FounderDashboard;

